import { User } from 'src/app/components/models/user.mode';
import { signalStore, withState, withMethods, withComputed, patchState } from '@ngrx/signals';
import { computed, inject } from '@angular/core';
import { PaymentService } from 'src/app/services/payment.service';
import { catchError, EMPTY, lastValueFrom, Observable, of, pipe, switchMap, tap } from 'rxjs';
import { tapResponse } from '@ngrx/operators';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { QuoteModel } from 'src/app/components/models/quote.model';

import { Asset } from 'src/app/components/models/asset.model';
import { UserService } from 'src/app/services/user.service';
import { PaymentRequestModel } from 'src/app/components/models/payment.request.model';
import { ToastrFactoryService } from 'src/app/services/toastr-factory.service';
import { TranslateService } from '@ngx-translate/core';
import { GetTxInfoResult } from 'src/app/components/models/get-tx-info-result.model';
import { Router } from '@angular/router';
import { AssetStore } from 'src/app/stores/asset.store';
import { firstValueFrom } from 'rxjs';
import { initialState, PaymentState } from '../models/payment.types';
import GetSanitizedImageUrlUtil from '@app/utils/get-sanitized-image-url-util';
import { stepsConfig } from '../utils/payment-steps.constants';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { PaymentForm } from '../models/payment.types';
import RoundUpUtil from 'src/app/utils/round-up-utils';
import { FormatPriceUtils } from 'src/app/utils/format-price-utils';
import { optionSelectsModel } from 'src/app/components/models/option-select.model';
import { formatNetworkName } from 'src/app/utils/network-utils';
import { AssetIdFormatterUtil } from 'src/app/utils/asset-id-utils';

// Extended state interface
interface ExtendedPaymentState extends PaymentState {
  // UI State
  isLoadingRates: boolean;
  flippedRates: boolean;
  network: string;
  refreshRatesTimerInSeconds: number;
  refreshQuotesTimerInSeconds: number;
  walletError: boolean;
  onlyOneAcceptedAsset: boolean;
  currentSettlingAssetName: string;
  currentPayingAssetName: string;
  disableSelectedNetworkButton: boolean;

  // Asset Collections
  assetOptions: Asset[];
  settlementAssetOptions: Asset[];
  cryptoAssets: Asset[];
  enabledNetworks: string[];
  currentOptionsSelects: {
    payingAsset: optionSelectsModel[];
  };

  // Form
  payment: FormGroup<PaymentForm>;

  // Timers
  refreshRatesInterval: any;
}

// Extended initial state
const extendedInitialState: ExtendedPaymentState = {
  ...initialState,
  isLoadingRates: false,
  flippedRates: false,
  network: '',
  refreshRatesTimerInSeconds: 15,
  refreshQuotesTimerInSeconds: 900,
  walletError: false,
  onlyOneAcceptedAsset: false,
  currentSettlingAssetName: '',
  currentPayingAssetName: '',
  disableSelectedNetworkButton: false,
  assetOptions: [] as Asset[],
  settlementAssetOptions: [] as Asset[],
  cryptoAssets: [] as Asset[],
  enabledNetworks: [] as string[],
  currentOptionsSelects: {
    payingAsset: [] as optionSelectsModel[],
  },
  payment: new FormGroup({
    amount: new FormControl('', { validators: [Validators.required], nonNullable: true }),
    targetAmount: new FormControl('', { validators: [Validators.required], nonNullable: true }),
    reference: new FormControl('', {
      validators: [Validators.maxLength(17)],
      nonNullable: true,
    }),
    transactionID: new FormControl('', { validators: [Validators.required], nonNullable: true }),
  }),
  refreshRatesInterval: null,
};

export const PaymentStore = signalStore(
  {
    providedIn: 'root',
  },
  withState(extendedInitialState),
  withComputed(
    ({
      paymentDetails,
      receiverConnections,
      paymentRequest,
      paymentQueryParams,
      targetUser,
      payerIsAuthenticated,
      amountInfo,
      stepsCompleted,
      receiverAcceptedAssets,
      assetOptions,
      settlementAssetOptions,
      enabledNetworks,
      network,
      isLoadingRates,
      flippedRates,
      refreshRatesTimerInSeconds,
      walletError,
      onlyOneAcceptedAsset,
      currentSettlingAssetName,
      currentPayingAssetName,
      disableSelectedNetworkButton,
      currentOptionsSelects,
      payment,
      noWallet,
      resumePayment,
      currentStep,
      txInfo,
      user,
    }) => {
      const receiverAcceptedAssetsComputed = computed(() => receiverAcceptedAssets());
      const receiverConnectionsComputed = computed(() => receiverConnections());
      const paymentDetailsComputed = computed(() => paymentDetails());
      const paymentRequestComputed = computed(() => paymentRequest());
      const paymentAmountInfoComputed = computed(() => amountInfo());
      const targetUserComputed = computed(() => targetUser?.());
      const hasConnections = computed(() => {
        const connections = receiverConnections();
        return connections !== null && connections.length > 0;
      });
      const hasAcceptedAssets = computed(() => {
        const acceptedAssets = receiverAcceptedAssets();
        return acceptedAssets !== null && acceptedAssets.length > 0;
      });
      const canProceedToPayment = computed(() => {
        const details = paymentDetailsComputed();
        return !!details.payerName && !!details.email && details.settlementAmount && details.settlementAmount > 0;
      });

      const itsFixedSettlementAsset = computed(() => {
        const request = paymentRequestComputed();
        const queryParams = paymentQueryParams();
        return request?.blockchainAssetId || queryParams.settlementAsset;
      });

      const itsFixedSettlementAmount = computed(() => {
        const request = paymentRequest();
        const queryParams = paymentQueryParams();
        return request?.amountNative || queryParams.amount;
      });

      const isAccessRestrictedBilling = computed(() => {
        return targetUserComputed()?.isAccessRestrictedBilling;
      });

      const steps = computed(() => {
        return stepsConfig.filter((step) => {
          switch (step.id) {
            case 'identification':
              if (payerIsAuthenticatedComputed()) {
                return false;
              } else if (paymentRequestComputed().requester?.email) {
                return false;
              }
              return true;
            default:
              return true;
          }
        });
      });

      const isPaymentStepValidComputed = computed(() => {
        const input = settlementAmountForInputComputed();
        const amount = Number(input);
        const isValidAmount = !isNaN(amount) && amount > 0;

        const hasWallet = !noWalletComputed();

        const payingAsset = payingAssetComputed();
        const requiresNetwork = payingAsset?.isMultiChain === true;
        const hasNetwork = !requiresNetwork || !!network();

        return isValidAmount && hasWallet && hasNetwork;
      });

      const ifNotPaymentRequestHasSameTargetUser = computed(() => {
        const request = paymentRequestComputed();
        if (!request?.bill) {
          return false;
        }
        const target = targetUserComputed();
        return request?.bill?.receiverUser?.id !== target?.id;
      });

      const isPaymentRequestReported = computed(() => {
        const request = paymentRequestComputed();
        if (!request) {
          return false;
        }
        return request?.bill?.paymentReports?.length > 0;
      });

      // New computed values for UI state
      const assetOptionsComputed = computed(() => assetOptions());
      const settlementAssetOptionsComputed = computed(() => settlementAssetOptions());
      const enabledNetworksComputed = computed(() => enabledNetworks());
      const networkComputed = computed(() => network());
      const isLoadingRatesComputed = computed(() => isLoadingRates());
      const flippedRatesComputed = computed(() => flippedRates());
      const refreshRatesTimerInSecondsComputed = computed(() => refreshRatesTimerInSeconds());
      const walletErrorComputed = computed(() => walletError());
      const onlyOneAcceptedAssetComputed = computed(() => onlyOneAcceptedAsset());
      const currentSettlingAssetNameComputed = computed(() => currentSettlingAssetName());
      const currentPayingAssetNameComputed = computed(() => currentPayingAssetName());
      const disableSelectedNetworkButtonComputed = computed(() => disableSelectedNetworkButton());
      const currentOptionsSelectsComputed = computed(() => currentOptionsSelects());
      const paymentFormComputed = computed(() => payment());

      // New computed values for UI optimization
      const settlementAssetTickerComputed = computed(() => paymentDetails().settlementAsset?.assetTicker || 'USD');

      const payingAssetTickerComputed = computed(() => paymentDetails().payingAsset?.assetTicker || 'BTC');

      const isCommaDecimalComputed = computed(
        () => false, // Default to false, will be handled in component
      );

      const settlementAmountComputed = computed(() => paymentDetails().settlementAmount);

      const assetValueComputed = computed(() => paymentDetails().assetValue);

      const payingAssetPriceComputed = computed(() => amountInfo().payingAssetPrice || 1);

      const assetConversionRateAmountComputed = computed(() => amountInfo().assetConversionRateAmount || 1);

      const settlementAssetComputed = computed(() => paymentDetails().settlementAsset);

      const payingAssetComputed = computed(() => paymentDetails().payingAsset);

      const ratesComputed = computed(() => paymentDetails().rates);

      const referenceComputed = computed(() => paymentDetails().reference);

      const selectedReceiverWalletComputed = computed(() => paymentDetails().selectedReceiverWallet);

      const noWalletComputed = computed(() => noWallet());

      const resumePaymentComputed = computed(() => resumePayment());

      const paymentQueryParamsComputed = computed(() => paymentQueryParams());

      const paymentRequestStateComputed = computed(() => paymentRequest());

      const targetUserStateComputed = computed(() => targetUser?.() || null);

      const payerIsAuthenticatedComputed = computed(() => payerIsAuthenticated());

      const currentStepComputed = computed(() => currentStep());

      const stepsCompletedComputed = computed(() => stepsCompleted());

      const txInfoComputed = computed(() => txInfo?.() || null);

      const userComputed = computed(() => user?.() || null);

      // Form validation computed
      const isFormValidComputed = computed(() => payment().valid);

      const formErrorsComputed = computed(() => {
        const form = payment();
        const errors: any = {};

        Object.keys(form.controls).forEach((key) => {
          const control = form.get(key);
          if (control?.errors) {
            errors[key] = control.errors;
          }
        });

        return errors;
      });

      // Asset processing computed
      const hasMultipleSettlementAssetsComputed = computed(() => settlementAssetOptions().length > 1);

      const hasMultipleAssetOptionsComputed = computed(() => assetOptions().length > 1);

      const hasSingleAssetOptionComputed = computed(() => assetOptions().length === 1);

      const hasEnabledNetworksComputed = computed(() => enabledNetworks().length > 0);

      const isMultiChainAssetComputed = computed(() => paymentDetails().payingAsset?.isMultiChain);

      const shouldShowNetworkSelectionComputed = computed(
        () => paymentDetails().payingAsset?.isMultiChain && enabledNetworks().length > 0,
      );

      const shouldShowRateSectionComputed = computed(() => paymentDetails().rates && paymentDetails().rates?.price);

      const shouldShowRateErrorComputed = computed(() => paymentDetails().rates && !paymentDetails().rates?.price);

      const shouldShowNoWalletErrorComputed = computed(() => noWallet());

      const shouldShowReferenceInputComputed = computed(
        () => !paymentRequest()?.bill?.invoiceReference && !paymentQueryParams()?.reference,
      );

      const shouldShowFixedSettlementAssetComputed = computed(() => itsFixedSettlementAsset());

      const shouldShowFixedSettlementAmountComputed = computed(() => itsFixedSettlementAmount());

      const shouldShowAssetSelectorComputed = computed(
        () => !itsFixedSettlementAsset() && settlementAssetOptions().length > 1,
      );

      const shouldShowAmountInputComputed = computed(() => !itsFixedSettlementAmount());

      // Additional computed values for template optimization
      const settlementAssetTickerForInputComputed = computed(() => paymentDetails().settlementAsset?.assetTicker || '');

      const payingAssetTickerForInputComputed = computed(() => paymentDetails().payingAsset?.assetTicker || '');

      const settlementAmountForInputComputed = computed(() => paymentDetails().settlementAmount || '');

      const referenceForInputComputed = computed(() => paymentDetails().reference || '');

      const assetValueForDisplayComputed = computed(() => paymentDetails().assetValue || 0);

      const settlementAmountForDisplayComputed = computed(() => paymentDetails().settlementAmount || 0);

      const payingAssetPriceForDisplayComputed = computed(() => amountInfo().payingAssetPrice || 1);

      const conversionRateForDisplayComputed = computed(() => amountInfo().assetConversionRateAmount || 1);

      const timerForDisplayComputed = computed(() => refreshRatesTimerInSeconds().toString());

      const isSameAssetsComputed = computed(() => {
        const payingAsset = paymentDetails().payingAsset?.assetTicker;
        const settlementAsset = paymentDetails().settlementAsset?.assetTicker;
        return payingAsset === settlementAsset;
      });

      const isFormValidForStepComputed = computed(() => payment().valid);

      const canProceedToNextStepComputed = computed(() => {
        const details = paymentDetails();
        return (
          !!details.settlementAmount && (!details.payingAsset?.isMultiChain || !!network()) && details.rates?.price
        );
      });

      // Event handling computed values
      const settlementAssetForSelectionComputed = computed(() => paymentDetails().settlementAsset);

      const payingAssetForSelectionComputed = computed(() => paymentDetails().payingAsset);

      const networkForSelectionComputed = computed(() => network());

      const flippedRatesForToggleComputed = computed(() => flippedRates());

      const isLoadingRatesForDisplayComputed = computed(() => isLoadingRates());

      const enabledNetworksForSelectionComputed = computed(() => enabledNetworks());

      const assetOptionsForSelectionComputed = computed(() => assetOptions());

      const settlementAssetOptionsForSelectionComputed = computed(() => settlementAssetOptions());

      // Form validation computed values
      const isFormValidForValidationComputed = computed(() => payment().valid);

      const formErrorsForValidationComputed = computed(() => {
        const form = payment();
        const errors: any = {};

        Object.keys(form.controls).forEach((key) => {
          const control = form.get(key);
          if (control?.errors) {
            errors[key] = control.errors;
          }
        });

        return errors;
      });

      // Asset processing computed values
      const hasMultipleSettlementAssetsForDisplayComputed = computed(() => settlementAssetOptions().length > 1);

      const hasMultipleAssetOptionsForDisplayComputed = computed(() => assetOptions().length > 1);

      const hasSingleAssetOptionForDisplayComputed = computed(() => assetOptions().length === 1);

      const hasEnabledNetworksForDisplayComputed = computed(() => enabledNetworks().length > 0);

      const isMultiChainAssetForDisplayComputed = computed(() => paymentDetails().payingAsset?.isMultiChain);

      // Rate display computed values
      const shouldShowRateSectionForDisplayComputed = computed(
        () => paymentDetails().rates && paymentDetails().rates?.price,
      );

      const shouldShowRateErrorForDisplayComputed = computed(
        () => paymentDetails().rates && !paymentDetails().rates?.price,
      );

      const shouldShowNoWalletErrorForDisplayComputed = computed(() => noWallet());

      // Input visibility computed values
      const shouldShowReferenceInputForDisplayComputed = computed(
        () => !paymentRequest()?.bill?.invoiceReference && !paymentQueryParams()?.reference,
      );

      const shouldShowFixedSettlementAssetForDisplayComputed = computed(() => itsFixedSettlementAsset());

      const shouldShowFixedSettlementAmountForDisplayComputed = computed(() => itsFixedSettlementAmount());

      const shouldShowAssetSelectorForDisplayComputed = computed(
        () => !itsFixedSettlementAsset() && settlementAssetOptions().length > 1,
      );

      const shouldShowAmountInputForDisplayComputed = computed(() => !itsFixedSettlementAmount());

      const shouldShowNetworkSelectionForDisplayComputed = computed(
        () => paymentDetails().payingAsset?.isMultiChain && enabledNetworks().length > 0,
      );

      return {
        isPaymentStepValidComputed,
        canProceedToPayment,
        paymentDetailsComputed,
        paymentRequestComputed,
        hasConnections,
        hasAcceptedAssets,
        itsFixedSettlementAmount,
        itsFixedSettlementAsset,
        isAccessRestrictedBilling,
        steps,
        isPaymentRequestReported,
        ifNotPaymentRequestHasSameTargetUser,
        paymentAmountInfoComputed,
        targetUserComputed,
        receiverAcceptedAssetsComputed,
        receiverConnectionsComputed,
        // New computed values
        assetOptionsComputed,
        settlementAssetOptionsComputed,
        enabledNetworksComputed,
        networkComputed,
        isLoadingRatesComputed,
        flippedRatesComputed,
        refreshRatesTimerInSecondsComputed,
        walletErrorComputed,
        onlyOneAcceptedAssetComputed,
        currentSettlingAssetNameComputed,
        currentPayingAssetNameComputed,
        disableSelectedNetworkButtonComputed,
        currentOptionsSelectsComputed,
        paymentFormComputed,
        settlementAssetTickerComputed,
        payingAssetTickerComputed,
        isCommaDecimalComputed,
        settlementAmountComputed,
        assetValueComputed,
        payingAssetPriceComputed,
        assetConversionRateAmountComputed,
        settlementAssetComputed,
        payingAssetComputed,
        ratesComputed,
        referenceComputed,
        selectedReceiverWalletComputed,
        noWalletComputed,
        resumePaymentComputed,
        paymentQueryParamsComputed,
        paymentRequestStateComputed,
        targetUserStateComputed,
        payerIsAuthenticatedComputed,
        currentStepComputed,
        stepsCompletedComputed,
        txInfoComputed,
        userComputed,
        isFormValidComputed,
        formErrorsComputed,
        hasMultipleSettlementAssetsComputed,
        hasMultipleAssetOptionsComputed,
        hasSingleAssetOptionComputed,
        hasEnabledNetworksComputed,
        isMultiChainAssetComputed,
        shouldShowNetworkSelectionComputed,
        shouldShowRateSectionComputed,
        shouldShowRateErrorComputed,
        shouldShowNoWalletErrorComputed,
        shouldShowReferenceInputComputed,
        shouldShowFixedSettlementAssetComputed,
        shouldShowFixedSettlementAmountComputed,
        shouldShowAssetSelectorComputed,
        shouldShowAmountInputComputed,
        settlementAssetTickerForInputComputed,
        payingAssetTickerForInputComputed,
        settlementAmountForInputComputed,
        referenceForInputComputed,
        assetValueForDisplayComputed,
        settlementAmountForDisplayComputed,
        payingAssetPriceForDisplayComputed,
        conversionRateForDisplayComputed,
        timerForDisplayComputed,
        isSameAssetsComputed,
        isFormValidForStepComputed,
        canProceedToNextStepComputed,
        settlementAssetForSelectionComputed,
        payingAssetForSelectionComputed,
        networkForSelectionComputed,
        flippedRatesForToggleComputed,
        isLoadingRatesForDisplayComputed,
        enabledNetworksForSelectionComputed,
        assetOptionsForSelectionComputed,
        settlementAssetOptionsForSelectionComputed,
        isFormValidForValidationComputed,
        formErrorsForValidationComputed,
        hasMultipleSettlementAssetsForDisplayComputed,
        hasMultipleAssetOptionsForDisplayComputed,
        hasSingleAssetOptionForDisplayComputed,
        hasEnabledNetworksForDisplayComputed,
        isMultiChainAssetForDisplayComputed,
        shouldShowRateSectionForDisplayComputed,
        shouldShowRateErrorForDisplayComputed,
        shouldShowNoWalletErrorForDisplayComputed,
        shouldShowReferenceInputForDisplayComputed,
        shouldShowFixedSettlementAssetForDisplayComputed,
        shouldShowFixedSettlementAmountForDisplayComputed,
        shouldShowAssetSelectorForDisplayComputed,
        shouldShowAmountInputForDisplayComputed,
        shouldShowNetworkSelectionForDisplayComputed,
      };
    },
  ),

  withMethods(
    (
      store,
      paymentService = inject(PaymentService),
      userService = inject(UserService),
      assetStore = inject(AssetStore),
      toastrFactory = inject(ToastrFactoryService),
      translate = inject(TranslateService),
      router = inject(Router),
      getSanitizedImageUrlUtil = inject(GetSanitizedImageUrlUtil),
      roundUpUtil = inject(RoundUpUtil),
      formatPriceUtils = inject(FormatPriceUtils),
      assetIdFormatterUtil = inject(AssetIdFormatterUtil),
    ) => ({
      updatePaymentDetails(details: Partial<PaymentState['paymentDetails']>) {
        const currentDetails = store.paymentDetails();
        patchState(store, { paymentDetails: { ...currentDetails, ...details } });
      },

      updateNoWallet(noWallet: boolean) {
        patchState(store, { noWallet });
      },

      updateAmountInfo(details: Partial<PaymentState['amountInfo']>) {
        const currentDetails = store.amountInfo();
        patchState(store, { amountInfo: { ...currentDetails, ...details } });
      },

      async updatePaymentRequest(paymentRequest: PaymentRequestModel) {
        const selectedAssetObject = await this.findAssetObject(paymentRequest.blockchainAssetId);
        this.updatePaymentDetails({
          settlementAsset: selectedAssetObject,
          settlementAmount: paymentRequest.amountNative,
          payerName: paymentRequest.requester?.name,
          email: paymentRequest.requester?.email,
          reference: paymentRequest.bill.invoiceReference,
        });
        this.setTargetUser(paymentRequest.creator as unknown as User);
        patchState(store, { paymentRequest });
      },

      setPaymentDetailsFromQuote(quote: QuoteModel): void {
        this.updatePaymentDetails({
          quote,
          rates: quote,
          payingAsset: { ...quote.to },
          settlementAsset: { ...quote.from },
          settlementAmount: quote.requestedAmount,
          paymentAmount: quote.price,
        });

        this.setIsResumePayment(true);
      },

      nextStep(step: string) {
        if (!store.paymentFormComputed().valid) {
          toastrFactory.error(translate.instant('formInvalid'));
          return;
        }
        this.clearTimer();
        const stepsCompleted = { ...store.stepsCompleted(), [step]: true };
        patchState(store, { currentStep: step, stepsCompleted });
      },

      setUser(user: User) {
        patchState(store, { user });
      },

      async setTargetUser(targetUser: User) {
        if (targetUser) {
          try {
            targetUser.profileImageUrl = await getSanitizedImageUrlUtil.getSanizedImageUrlByUserId(targetUser.id);
          } catch (error) {
            console.error('Error fetching user profile image:', error);
            targetUser.profileImageUrl = undefined;
          }

          patchState(store, { targetUser: targetUser });
        }
      },

      setIsResumePayment(resumedPayment: boolean) {
        patchState(store, { resumePayment: resumedPayment });
      },

      reset() {
        patchState(store, extendedInitialState);
      },

      async findAssetObject(assetId: string) {
        if (assetStore.entities().length === 0) {
          await assetStore.loadAssets();
        }

        return assetStore.entities().find((asset) => asset.id === assetId);
      },

      updateTxInfo(txInfo: GetTxInfoResult) {
        patchState(store, { txInfo });
      },

      fetchQuote: rxMethod<any>(
        pipe(
          switchMap(() => {
            const payingAsset = store.paymentDetails().payingAsset?.id || '';
            const settlementAsset = store.paymentDetails().settlementAsset?.id || '';
            return paymentService.getPrice(settlementAsset, payingAsset).pipe(
              tapResponse({
                next: (quote: any) => {
                  patchState(store, { paymentDetails: { quote: quote } });
                },
                error: (error) => {
                  console.log(error);
                },
              }),
            );
          }),
        ),
      ),
      async asyncHasConnections() {
        const connections = await this.fetchReceiverConnections();
        return connections !== null && connections.length > 0;
      },

      async asyncHasAcceptedAssets() {
        const acceptedAssets = await this.fetchReceiverAcceptedAssets();
        return acceptedAssets !== null && Array.isArray(acceptedAssets) && acceptedAssets.length > 0;
      },

      async fetchReceiverAcceptedAssets(): Promise<Asset[] | void> {
        const targetUserId = store.targetUser?.()?.id;
        try {
          const acceptedAssets = await lastValueFrom(userService.getAcceptedAssetsById(targetUserId));
          patchState(store, { receiverAcceptedAssets: acceptedAssets });

          this.checkIfUserAcceptSettlementAsset(acceptedAssets);

          return acceptedAssets;
        } catch (error) {
          console.error('Failed to fetch receiver accepted assets:', error);
          throw error;
        }
      },

      checkIfUserAcceptSettlementAsset(acceptedAssets: Asset[]) {
        const settlementAsset = store.paymentDetails().settlementAsset;
        const isFixedSettlement = store.itsFixedSettlementAsset();

        if (isFixedSettlement && settlementAsset && !acceptedAssets.some((asset) => asset.id === settlementAsset.id)) {
          console.warn('Receiver does not accept the selected settlement asset');
          const translatedMessage = translate.instant('receiverDoesNotAcceptAsSettlementAsset', {
            assetId: settlementAsset.assetTicker,
          });
          toastrFactory.error(translatedMessage);
          this.reset();
          router.navigate(['overview']);
          return false;
        }

        return true;
      },

      async fetchReceiverConnections(): Promise<any> {
        const targetUserId = store.targetUser?.()?.id;
        if (!targetUserId) {
          return;
        }
        try {
          const connections = await lastValueFrom(userService.getReceiverConnections(targetUserId));
          patchState(store, { receiverConnections: connections });
          return connections;
        } catch (error) {
          console.error(error);
          throw error;
        }
      },

      fetchRates(): Observable<any> {
        return of(null).pipe(
          switchMap(() => {
            const paymentDetails = store.paymentDetails();
            const { payingAsset, settlementAsset } = paymentDetails;

            if (!payingAsset || !settlementAsset) {
              console.warn('Missing payingAsset or settlementAsset');
              return EMPTY;
            }

            const sameAssets = payingAsset.assetTicker === settlementAsset.assetTicker;

            let settlementAssetId = settlementAsset.isMultiChain
              ? `ETH.${settlementAsset.assetTicker}`
              : settlementAsset.id;

            if (settlementAssetId === undefined) {
              settlementAssetId = `${settlementAsset.assetTicker}.${settlementAsset.assetTicker}`;
            }

            return paymentService.getPrice(payingAsset.id, settlementAssetId).pipe(
              tap((rates) => {
                if (!rates?.price && !sameAssets) {
                  toastrFactory.error(
                    translate.instant('PAYMENT_REPORT.weEncounteredATemporaryProblemFetchingTheRate'),
                  );
                }

                if (sameAssets) {
                  rates.price = 1;
                }

                patchState(store, {
                  paymentDetails: {
                    ...store.paymentDetails(),
                    rates,
                  },
                });
              }),
              catchError((error) => {
                console.error('Error fetching rates:', error);
                return EMPTY;
              }),
            );
          }),
        );
      },

      async fetchRatesAsync(): Promise<any> {
        return lastValueFrom(this.fetchRates());
      },

      getUnusedQuoteById(quoteId: string) {
        return paymentService.getUnusedQuoteById(quoteId);
      },

      validatePaymentDetails() {
        const details = store.paymentDetails();
        return !!details.payerName && !!details.email && details.settlementAmount && details.settlementAmount > 0;
      },

      updatePayerAuthentication(isAuthenticated: boolean) {
        patchState(store, { payerIsAuthenticated: isAuthenticated });
      },

      async fetchUserWallet() {
        const paymentDetails = store.paymentDetails();
        const targetUser = store.targetUser?.();
        const paymentRequest = store.paymentRequest?.();

        if (!targetUser?.email || !paymentDetails.payingAsset) {
          return;
        }

        try {
          this.updatePaymentDetails({ selectedReceiverWallet: undefined });

          const response = await firstValueFrom(
            paymentService.getUserWallets(
              targetUser.email,
              paymentDetails.payingAsset.id,
              paymentRequest.bill?.shortId,
            ),
          );

          if (!response.cryptoAddress && !paymentDetails.payingAsset.isMultiChain) {
            const toastrMessage = translate.instant('userTemporarilyUnableToReceivePayments', {
              assetName: paymentDetails.payingAsset.assetTicker,
            });
            this.updateNoWallet(true);
            toastrFactory.error(toastrMessage, '', { enableHtml: true });
            return false;
          }

          this.updateNoWallet(false);
          this.updatePaymentDetails({ selectedReceiverWallet: response as any });
          return true;
        } catch (error: any) {
          console.error('Error fetching wallet:', error);

          const errorMessage = error?.error;
          if (errorMessage?.detail?.includes('No user found for this Nick:')) {
            toastrFactory.error(translate.instant('PAYMENT_REPORT.thisPaymentRequestIsNotValid'), '', {
              timeOut: 5000,
            });
          } else {
            const toastrMessage = translate.instant('userTemporarilyUnableToReceivePayments', {
              assetName: paymentDetails.payingAsset.assetTicker,
            });
            toastrFactory.error(toastrMessage, '', { enableHtml: true });
          }

          this.updatePaymentDetails({ selectedReceiverWallet: undefined });
          return false;
        }
      },

      // UI State Management Methods
      updateIsLoadingRates(isLoading: boolean) {
        patchState(store, { isLoadingRates: isLoading });
      },

      updateFlippedRates(flipped: boolean) {
        patchState(store, { flippedRates: flipped });
      },

      updateNetwork(network: string) {
        patchState(store, { network });
      },

      updateRefreshRatesTimer(seconds: number) {
        patchState(store, { refreshRatesTimerInSeconds: seconds });
      },

      updateWalletError(error: boolean) {
        patchState(store, { walletError: error });
      },

      updateOnlyOneAcceptedAsset(onlyOne: boolean) {
        patchState(store, { onlyOneAcceptedAsset: onlyOne });
      },

      updateCurrentSettlingAssetName(name: string) {
        patchState(store, { currentSettlingAssetName: name });
      },

      updateCurrentPayingAssetName(name: string) {
        patchState(store, { currentPayingAssetName: name });
      },

      updateDisableSelectedNetworkButton(disabled: boolean) {
        patchState(store, { disableSelectedNetworkButton: disabled });
      },

      // Asset Management Methods
      updateAssetOptions(assets: Asset[]) {
        patchState(store, { assetOptions: assets });
      },

      updateSettlementAssetOptions(assets: Asset[]) {
        patchState(store, { settlementAssetOptions: assets });
      },

      updateCryptoAssets(assets: Asset[]) {
        patchState(store, { cryptoAssets: assets });
      },

      updateEnabledNetworks(networks: string[]) {
        patchState(store, { enabledNetworks: networks });
      },

      updateCurrentOptionsSelects(options: { payingAsset: optionSelectsModel[] }) {
        patchState(store, { currentOptionsSelects: options });
      },

      // Form Management Methods
      updatePaymentForm(form: FormGroup<PaymentForm>) {
        patchState(store, { payment: form });
      },

      updateAmountFormControls(settlementAmount: string, assetValue: string) {
        const currentForm = store.payment();
        currentForm.controls['targetAmount'].setValue(settlementAmount);
        currentForm.controls['amount'].setValue(assetValue);
        patchState(store, { payment: currentForm });
      },

      // Timer Management Methods
      clearTimer() {
        const currentInterval = store.refreshRatesInterval();
        if (currentInterval) {
          clearInterval(currentInterval);
          patchState(store, { refreshRatesInterval: null });
        }
      },

      setRatesTimeout() {
        this.clearTimer();
        const interval = setInterval(async () => {
          const currentTimer = store.refreshRatesTimerInSeconds();
          const newTimer = currentTimer - 1;

          if (newTimer === 0) {
            const rates = store.paymentDetails().rates;
            if (rates?.price) {
              const payingAsset = store.paymentDetails().payingAsset;
              const settlementAsset = store.paymentDetails().settlementAsset;
              if (payingAsset?.id !== settlementAsset?.id) {
                await this.fetchRatesAsync();
              }
            }
            this.clearTimer();
            // Restart the timer automatically
            this.setRatesTimeout();
          } else {
            patchState(store, { refreshRatesTimerInSeconds: newTimer });
          }
        }, 1000);

        patchState(store, {
          refreshRatesInterval: interval,
          refreshRatesTimerInSeconds: 15,
        });
      },

      restartRatesTimeout() {
        this.setRatesTimeout();
      },

      // Asset Processing Methods
      parseAmount(amount: any): number {
        if (!amount) return 0;
        return parseFloat(amount.toString().replace(/\,/g, '')) || 0;
      },

      updateAsset() {
        const settlementAmount = store.paymentDetails().settlementAmount;
        const assetConversionRateAmount = this.parseAmount(store.amountInfo().assetConversionRateAmount) || 1;

        if (!settlementAmount) {
          return;
        }

        this.updatePaymentDetails({ settlementAmount });

        const settlementAmountCurrentValue = this.parseAmount(settlementAmount.toString());
        const ratesAmount = settlementAmountCurrentValue / assetConversionRateAmount;

        const decimalPrecision =
          store.paymentDetails().rates?.from?.decimalPrecisionUI || assetStore.decimalPlacesFor()['ETH'];

        const roundedValue = roundUpUtil.roundUpAmount(ratesAmount, decimalPrecision);
        const formattedAssetValue = this.formatPrice(roundedValue);
        const formattedSettlementAmount = this.formatPrice(settlementAmount.toString(), true);

        this.updatePaymentDetails({ assetValue: formattedAssetValue });
        this.updateAmountFormControls(formattedSettlementAmount, formattedAssetValue);
      },

      // Network Selection Methods
      selectedNetwork() {
        const payingAsset = store.paymentDetails().payingAsset;

        if (payingAsset?.isMultiChain) {
          const enabledNetworks = new Set<string>();

          store.receiverConnections()?.forEach((connection: any) => {
            if (connection.assetTicker === store.paymentDetails().payingAsset?.assetTicker) {
              if (connection.id.includes('ETH')) {
                enabledNetworks.add('ETH');
              } else if (connection.id.includes('MATIC')) {
                enabledNetworks.add('MATIC');
              } else {
                enabledNetworks.add('TRC');
              }
            }
          });

          const enabledNetworksArray = Array.from(enabledNetworks).sort();
          this.updateEnabledNetworks(enabledNetworksArray);

          console.log(enabledNetworksArray);
          if (!enabledNetworksArray.includes(payingAsset.assetChain)) {
            this.updatePaymentDetails({ network: '' });
          }
        }
      },

      // Asset Selection Methods
      getTargetAsset(targetAsset: string, settlementAsset?: boolean): Asset | undefined {
        if (!targetAsset) return undefined;

        const resolvedNetwork = settlementAsset ? 'ETH' : store.network() || 'ETH';

        return assetStore
          .entities()
          .find(({ isMultiChain, assetTicker, assetChain }) =>
            isMultiChain ? assetTicker === targetAsset && assetChain === resolvedNetwork : assetTicker === targetAsset,
          );
      },

      setSelectedAssetsName(asset: any, settleAsset?: boolean) {
        if (settleAsset) {
          this.updateCurrentSettlingAssetName(asset.assetName);
        } else {
          this.updateCurrentPayingAssetName(asset.assetName);
        }
      },

      // Rate Management Methods
      async changeTargetCurrency() {
        this.updateIsLoadingRates(true);
        this.updateRefreshRatesTimer(15);
        this.clearTimer();

        const settlementAsset = this.getTargetAsset(store.paymentDetails().settlementAsset?.assetTicker || '', true);
        const payingAsset = this.getTargetAsset(store.paymentDetails().payingAsset?.assetTicker || '');

        this.updatePaymentDetails({ settlementAsset, payingAsset });
        this.selectedNetwork();

        try {
          await this.fetchRatesAsync();
          this.setRatesTimeout();
          this.testUpdatedAndOutdatedValues(store.paymentDetails().rates);
        } finally {
          this.updateIsLoadingRates(false);
        }
      },

      testUpdatedAndOutdatedValues(response?: any) {
        let payingAssetPrice = store.amountInfo().payingAssetPrice;
        payingAssetPrice = response.price;

        this.updateAmountInfo({
          payingAssetPrice,
          assetConversionRateAmount: payingAssetPrice,
          targetCurrencyQuote: '',
        });

        this.updateAsset();
      },

      // Asset Initialization Methods
      async initializeReceiverAssets() {
        const targetUserId = store.targetUser?.()?.id;
        if (targetUserId) {
          await this.fetchReceiverAcceptedAssets();
          await this.fetchReceiverConnections();
        }

        if (!store.paymentRequest()) {
          store.receiverAcceptedAssets()?.forEach((fiat: any) => {
            const currentOptions = store.currentOptionsSelects();
            currentOptions.payingAsset.push({
              name: fiat.assetTicker,
              value: fiat.assetTicker,
            });
            this.updateCurrentOptionsSelects(currentOptions);
          });
        }

        const settlementAsset = store.paymentDetails().settlementAsset || store.receiverAcceptedAssets()?.[0];
        if (settlementAsset) {
          this.updatePaymentDetails({ settlementAsset });
        }

        if (store.receiverAcceptedAssets()?.length === 1) {
          this.updateOnlyOneAcceptedAsset(true);
        }

        this.updateAssetOptions([]);

        if (store.receiverConnections()) {
          store.receiverConnections()?.forEach(async (asset: any) => {
            try {
              const currentAssetOptions = store.assetOptions();
              const hasAsset = currentAssetOptions.some(
                (assetConnection) => assetConnection?.assetTicker === asset.assetTicker,
              );
              if (!hasAsset) {
                currentAssetOptions.push(asset);
              }

              if (currentAssetOptions.length > 1) {
                currentAssetOptions.sort((a, b) => {
                  const result = a?.assetTicker.localeCompare(b?.assetTicker);
                  return result === 0 ? a?.assetTicker.localeCompare(b?.assetTicker) : result;
                });
              }

              const payingAssetTicker =
                store.paymentDetails().payingAsset?.assetTicker || currentAssetOptions[0].assetTicker;
              this.updatePaymentDetails({ payingAsset: { assetTicker: payingAssetTicker } as Asset });

              assetStore?.entities()?.forEach((asset: any) => {
                if (
                  asset.assetTicker === payingAssetTicker ||
                  asset.assetTicker === store.paymentDetails().payingAsset?.assetTicker
                ) {
                  this.updatePaymentDetails({ payingAsset: asset });
                  this.setSelectedAssetsName(asset, true);
                }
              });

              this.updateAssetOptions(currentAssetOptions);
            } catch (e) {
              console.error('Error processing receiver connection:', e);
            }
          });
        }

        const fiatAssets: Asset[] = [];
        const cryptoAssets: Asset[] = [];

        assetStore.entities().forEach((asset: Asset) => {
          const userAcceptsAsset = store
            .receiverAcceptedAssets()
            ?.some((acceptedAsset: any) => acceptedAsset.id === asset.id);

          if (asset.isFiat) {
            if (store.receiverAcceptedAssets()?.length === 0 || userAcceptsAsset) {
              fiatAssets.push(asset);
            }
          } else {
            const isExcludedChain = asset.assetChain === 'MATIC' || asset.assetChain === 'TRC';
            const alreadyAdded = cryptoAssets.some((savedAsset) => savedAsset.assetTicker === asset.assetTicker);

            if (
              (store.receiverAcceptedAssets()?.length === 0 && !isExcludedChain) ||
              (userAcceptsAsset && !alreadyAdded)
            ) {
              cryptoAssets.push(asset);
            }
          }
        });

        this.updateSettlementAssetOptions([...fiatAssets, ...cryptoAssets]);

        const settlementAssetTicker =
          store.paymentRequest()?.blockchainAssetId.split('.')[1] ||
          store.paymentDetails().settlementAsset?.assetTicker ||
          store.settlementAssetOptions()?.[0]?.assetTicker;

        this.updatePaymentDetails({ settlementAsset: { assetTicker: settlementAssetTicker } as Asset });

        assetStore?.entities()?.forEach((asset: any) => {
          const payingAssetTicker = store.paymentDetails().payingAsset?.assetTicker;
          if (
            asset.assetTicker === payingAssetTicker ||
            asset.assetTicker === store.paymentDetails().payingAsset?.assetTicker
          ) {
            this.updatePaymentDetails({ payingAsset: asset });
            this.setSelectedAssetsName(asset, true);
          }
        });

        this.startTimerMonitoring(() => true);
      },

      // Wallet Management Methods
      async getUserWallets(itsFromInput: boolean, changeNetwork?: boolean) {
        const payingAssetTicker = store.paymentDetails().payingAsset?.assetTicker;

        if (changeNetwork && (payingAssetTicker === 'USDC' || payingAssetTicker === 'USDT')) {
          const assetWithNetwork = assetStore
            .entities()
            .find((asset) => asset.assetTicker == payingAssetTicker && asset.assetChain === store.network());
          this.updatePaymentDetails({ payingAsset: assetWithNetwork });
          this.selectedNetwork();
        }

        this.updateDisableSelectedNetworkButton(false);

        try {
          const success = await this.fetchUserWallet();

          if (success) {
            this.chooseWallet(itsFromInput);
            this.updateWalletError(false);
            return;
          }
        } catch (error) {
          console.log('Error fetching user wallets:', error);
        }

        this.updateWalletError(true);
        this.chooseWallet(itsFromInput);
      },

      async chooseWallet(itsFromInput: boolean) {
        this.updateAmountInfo({
          assetConversionRateAmount: store.amountInfo().payingAssetPrice,
        });

        if (itsFromInput) {
          await this.changeTargetCurrency();
        }
      },

      // Utility Methods
      sameAssets(): boolean {
        const payingAsset = store.paymentDetails().payingAsset?.assetTicker;
        const settlementAsset = store.paymentDetails().settlementAsset?.assetTicker;
        return payingAsset === settlementAsset;
      },

      validNetwork(): boolean {
        const network = store.paymentDetails().payingAsset?.assetChain;
        return store.enabledNetworks().includes(network || '');
      },

      isValidStep(): boolean {
        return (
          !!store.paymentDetails().settlementAmount &&
          (!store.paymentDetails().payingAsset?.isMultiChain || !!store.network()) &&
          store.paymentDetails().rates?.price
        );
      },

      // Utility methods for formatting
      formatPrice(value: string | number, payingAsset?: boolean): string {
        const assetTicker = payingAsset
          ? store.paymentDetails().settlementAsset?.assetTicker
          : store.paymentDetails().payingAsset?.assetTicker;

        if (!value || !assetTicker) return '';

        return formatPriceUtils.formatPrice(value, assetTicker || 'USD');
      },

      getNetworkName(anyNetwork?: string) {
        const networkId = store.paymentDetails().payingAsset?.assetChain || '';
        return formatNetworkName(anyNetwork || networkId);
      },

      formatTimer(secondsString: string): string {
        const sec_num = parseInt(secondsString, 10);
        const hours = Math.floor(sec_num / 3600);
        let minutes = Math.floor((sec_num - hours * 3600) / 60);
        let seconds = sec_num - hours * 3600 - minutes * 60;

        if (minutes < 10) {
          minutes = Number(`0${minutes}`);
        }
        if (seconds < 10) {
          seconds = Number(`0${seconds}`);
        }

        return ` ${minutes >= 10 ? '' : '0'}${minutes}:${seconds.toString().length < 2 ? '0' + seconds : seconds}`;
      },

      getCoinMarketTooltip() {
        return translate.instant('thisConversionRatesComesFrom', {
          payingAsset: store.paymentDetails().payingAsset?.assetTicker,
          settlementAsset: store.paymentDetails().settlementAsset?.assetTicker,
          lineBreak: '\n\n',
        });
      },

      // Component-specific methods moved from component
      updateReference(): void {
        const reference = store.paymentDetails().reference?.slice(0, 17);
        this.updatePaymentDetails({ reference: reference || '' });
      },

      // async setInitalPayingAsset(): Promise<void> {
      //   const payingAsset = store.paymentDetails().payingAsset?.assetTicker || store.receiverAcceptedAssets()?.[0]?.assetTicker;
      //   if (payingAsset) {
      //     this.updatePaymentDetails({ payingAsset: { assetTicker: payingAsset } as Asset });
      //     this.setSelectedAssetsName({ assetTicker: payingAsset } as Asset, false);
      //   }
      // },

      async changeNetwork(enabledNetwork: string): Promise<void> {
        if (store.disableSelectedNetworkButton()) return;

        const networkName = translate.instant(`${this.getNetworkName(enabledNetwork).toLowerCase()}Network`);
        const translatedMessage = translate.instant('youHaveSelectedTheNetworkSendingFundsThrought', { networkName });

        if (store.network() !== enabledNetwork) {
          // Note: toastrFactory doesn't have clear method, we'll handle this in the component
          toastrFactory.success(translatedMessage);
        }

        this.updateNetwork(enabledNetwork);
        this.updateDisableSelectedNetworkButton(true);

        await this.getUserWallets(true, true);
      },

      changingSelectedAssetsOnUi(payingAssetTicker: string): void {
        assetStore?.entities()?.forEach((asset: any) => {
          if (
            asset.assetTicker === payingAssetTicker ||
            asset.assetTicker === store.paymentDetails().payingAsset?.assetTicker
          ) {
            this.updatePaymentDetails({ payingAsset: asset });
            this.setSelectedAssetsName(asset, true);
          }
        });
      },

      chooseUserWallet(itsFromInput: boolean, payingAssetTicker: string, value?: number): void {
        this.updateEnabledNetworks([]);
        this.selectedNetwork();

        const payingAsset = assetStore.entities().find((asset) => asset.assetTicker === payingAssetTicker);

        if (!payingAsset?.isMultiChain) {
          this.updatePaymentDetails({ payingAsset });
        }

        if (value) {
          this.updateAmountInfo({ assetConversionRateAmount: value });
        }

        this.updateAsset();
        this.updateRefreshRatesTimer(15);
        this.getUserWallets(itsFromInput, false);
      },

      generateReturnLink(): string {
        const baseUrl = window.location.origin;

        const targetUser = store.targetUser?.();
        const shortId = store.paymentRequest?.()?.bill?.shortId;

        const params = new URLSearchParams();

        // Only add parameters if they have values
        if (store.paymentDetailsComputed().quote?.id) {
          params.append('quoteId', store.paymentDetailsComputed().quote?.id || '');
        }

        const name = store.paymentDetailsComputed().payerName;
        if (name) {
          params.append('name', name);
        }

        const email = store.paymentDetailsComputed().email;
        if (email) {
          params.append('email', email);
        }

        // Add paymentId if it's a payment request and has a value
        if (shortId) {
          params.append('paymentId', shortId || '');
        }

        const receiverPath = targetUser?.shortId || 'unknown';
        const newUrl = `${baseUrl}/payment-report/${receiverPath}?${params.toString()}`;

        // Update the current URL without reloading the page
        window.history.pushState({}, '', newUrl);

        return newUrl;
      },

      // Timer monitoring method
      startTimerMonitoring(isAlive: () => boolean): void {
        const checkTimer = () => {
          if (isAlive() && store.refreshRatesTimerInSeconds() === 0) {
            // If timer is at 0 and component is alive, restart it
            this.restartRatesTimeout();
          }
          if (isAlive()) {
            setTimeout(checkTimer, 1000);
          }
        };
        setTimeout(checkTimer, 1000);
      },

      // Asset placeholder generation
      generatePlaceholderZerosForAsset(assetId = ''): string {
        return assetIdFormatterUtil.generatePlaceholderZerosForAsset(assetId);
      },

      // Template utility methods for cleaner event handling
      handleSettlementAssetSelection(assetTicker: string): void {
        const asset = this.getTargetAsset(assetTicker, true);
        this.updatePaymentDetails({ settlementAsset: asset });
        this.changeTargetCurrency();
        this.changingSelectedAssetsOnUi(store.payingAssetTickerComputed());
      },

      handleInitialPayingAssetSelection(): void {
        const asset = this.getTargetAsset(store.receiverConnections()?.[0]?.assetTicker || '');
        if (asset) {
          this.updatePaymentDetails({ payingAsset: asset });
          this.chooseUserWallet(true, asset.assetTicker);
          this.changingSelectedAssetsOnUi(asset.assetTicker);
        }
      },

      handlePayingAssetSelection(assetTicker: string): void {
        this.updateNetwork('');
        const asset = this.getTargetAsset(assetTicker);
        this.updatePaymentDetails({ payingAsset: asset });
        this.chooseUserWallet(true, store.payingAssetTickerComputed());
        this.changingSelectedAssetsOnUi(store.payingAssetTickerComputed());
      },

      handleSettlementAmountChange(amount: string): void {
        this.updatePaymentDetails({ settlementAmount: parseFloat(amount) || 0 });
        this.updateAsset();
      },

      handleReferenceChange(reference: string): void {
        this.updatePaymentDetails({ reference });
        this.updateReference();
      },

      handleNetworkSelection(networkName: string): void {
        this.updateNetwork(networkName);
        this.getUserWallets(true, true);
      },

      handleRateFlip(): void {
        this.updateFlippedRates(!store.flippedRatesComputed());
      },

      handleAssetUpdate(): void {
        this.updateAsset();
      },

      handleTimerRestart(): void {
        this.restartRatesTimeout();
      },

      // Validation utility methods
      validateFormForStep(): boolean {
        return store.isFormValidComputed();
      },

      validateCanProceedToNextStep(): boolean {
        return store.canProceedToNextStepComputed();
      },

      validateSameAssets(): boolean {
        return store.isSameAssetsComputed();
      },

      validateValidNetwork(): boolean {
        return this.validNetwork();
      },

      validateIsValidStep(): boolean {
        return this.isValidStep();
      },
    }),
  ),
);
